import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Format a YYYY-MM-DD date string as a local date without timezone shift.
 * Using new Date('YYYY-MM-DD') parses as UTC and can shift by one day.
 * This function constructs a local date directly from the components.
 */
export function formatLocalDate(dateStr: string, options?: Intl.DateTimeFormatOptions): string {
  if (!dateStr) return '';
  const [y, m, d] = dateStr.split('-').map(Number);
  const localDate = new Date(y, (m || 1) - 1, d || 1);
  return localDate.toLocaleDateString(undefined, options);
}

/**
 * Parse a YYYY-MM-DD date string as a local date without timezone shift.
 * Returns a Date object set to local midnight, avoiding UTC parsing issues.
 */
export function parseLocalDate(dateStr: string | null | undefined): Date | null {
  if (!dateStr) return null;
  // Handle both YYYY-MM-DD and ISO datetime strings
  const dateOnly = dateStr.split('T')[0];
  const [y, m, d] = dateOnly.split('-').map(Number);
  if (isNaN(y) || isNaN(m) || isNaN(d)) return null;
  return new Date(y, m - 1, d);
}

/**
 * Format a date string (YYYY-MM-DD) to YYYY-MM-DD for input fields without timezone shift.
 */
export function formatDateForInput(dateStr: string | null | undefined): string {
  if (!dateStr) return '';
  const date = parseLocalDate(dateStr);
  if (!date) return '';
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Format git commit date in Pacific timezone
 */
export function formatCommitDate(dateString: string): string {
  if (!dateString || dateString === 'unknown') return dateString;
  
  try {
    // Parse the git date string (handles various formats)
    const date = new Date(dateString);
    
    // Check if date is valid
    if (isNaN(date.getTime())) return dateString;
    
    // Format in Pacific timezone using Intl.DateTimeFormat
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: 'America/Los_Angeles',
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
      timeZoneName: 'short'
    });
    
    return formatter.format(date);
  } catch (e) {
    return dateString;
  }
}

/**
 * Convert Pacific time (date and time strings) to UTC Date object
 * @param dateStr - Date string in YYYY-MM-DD format (Pacific time)
 * @param timeStr - Time string in HH:mm format (Pacific time)
 * @returns Date object in UTC
 */
export function convertPacificToUTC(dateStr: string, timeStr: string): Date {
  const [year, month, day] = dateStr.split("-").map(Number);
  const [hour, minute] = timeStr.split(":").map(Number);
  if (![year, month, day, hour, minute].every(Number.isFinite)) return new Date(NaN);

  // Take the wall-clock reading as if it were UTC, then shift by Pacific's
  // offset at that instant. A second pass settles the reading on a day the
  // clocks change. (The previous version built an ISO string with hour + 8,
  // which is not a valid hour after 4 PM - "Invalid time value" on any
  // evening post, 6 Sep 2026.)
  const wall = Date.UTC(year, month - 1, day, hour, minute);
  let utc = wall;
  for (let pass = 0; pass < 2; pass++) {
    utc = wall + pacificOffsetMinutes(new Date(utc)) * 60_000;
  }
  return new Date(utc);
}

/** Minutes to add to a Pacific wall-clock reading to reach UTC (420 in summer, 480 in winter) at the given instant. */
function pacificOffsetMinutes(at: Date): number {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: "America/Los_Angeles",
    hourCycle: "h23",
    year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit",
  }).formatToParts(at);
  const get = (type: string) => Number(parts.find((p) => p.type === type)?.value ?? 0);
  const wallAsUtc = Date.UTC(get("year"), get("month") - 1, get("day"), get("hour"), get("minute"));
  return Math.round((at.getTime() - wallAsUtc) / 60_000);
}

/**
 * Format UTC date as Pacific time string (returns date and time separately)
 * @param utcDate - Date object in UTC
 * @returns Object with dateStr (YYYY-MM-DD) and timeStr (HH:mm) in Pacific timezone
 */
export function formatPacificTime(utcDate: Date): { dateStr: string; timeStr: string } {
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/Los_Angeles',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  });
  
  const parts = formatter.formatToParts(utcDate);
  const year = parts.find(p => p.type === 'year')?.value || '';
  const month = parts.find(p => p.type === 'month')?.value || '';
  const day = parts.find(p => p.type === 'day')?.value || '';
  const hour = parts.find(p => p.type === 'hour')?.value || '';
  const minute = parts.find(p => p.type === 'minute')?.value || '';
  
  return {
    dateStr: `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`,
    timeStr: `${hour.padStart(2, '0')}:${minute.padStart(2, '0')}`
  };
}

/**
 * A moment as the office writes it: "09-06-26 8:45 PM" (MM-DD-YY, 12-hour
 * clock, Pacific). The house style for any date and time shown on screen;
 * formatPacificTime above stays for feeding date/time inputs.
 */
export function formatPacificDisplay(utcDate: Date): string {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: "America/Los_Angeles",
    year: "2-digit", month: "2-digit", day: "2-digit",
    hour: "numeric", minute: "2-digit", hour12: true,
  }).formatToParts(utcDate);
  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? "";
  return `${get("month")}-${get("day")}-${get("year")} ${get("hour")}:${get("minute")} ${get("dayPeriod").toUpperCase()}`;
}

/**
 * Parse Pacific date/time strings and return UTC Date
 * Alias for convertPacificToUTC for consistency
 */
export function parsePacificDateTime(dateStr: string, timeStr: string): Date {
  return convertPacificToUTC(dateStr, timeStr);
}